import React, { useState } from "react";
import { ArrowLeft, Copy, Trash2, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { getErrorLog, clearErrorLog } from "../lib/errorLog";

/**
 * On-device error log viewer. Testers can email us their log when reporting
 * bugs so we can see what actually went wrong (no Sentry account required).
 */
export default function Debug() {
  const [entries, setEntries] = useState(() => getErrorLog());

  const refresh = () => setEntries(getErrorLog());

  const copy = async () => {
    const text = entries
      .map((e) => `[${e.ts}] (${e.source}) ${e.url}\n${e.message}`)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(text || "(no errors logged)");
      toast.success("Logs copied — paste into the bug report email");
    } catch (_) {
      toast.error("Couldn't copy — long-press an entry to copy manually");
    }
  };

  const clear = () => {
    clearErrorLog();
    setEntries([]);
    toast.success("Log cleared");
  };

  return (
    <div data-testid="debug-page" className="min-h-screen bg-[#F7F5F0] text-[#2C332A] px-4 py-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/dashboard"
          className="flex items-center gap-2 text-[#5F7161] hover:text-[#2C332A]"
          data-testid="debug-back-link"
        >
          <ArrowLeft size={18} strokeWidth={1.6} />
          <span className="text-sm">Back</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copy}
            data-testid="debug-copy-btn"
            className="rounded-full"
          >
            <Copy size={14} strokeWidth={1.6} className="mr-1.5" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clear}
            data-testid="debug-clear-btn"
            className="rounded-full text-[#A0454E]"
          >
            <Trash2 size={14} strokeWidth={1.6} className="mr-1.5" />
            Clear
          </Button>
        </div>
      </div>

      <p className="text-xs uppercase tracking-[0.2em] text-[#8AA48F] mb-2">Diagnostics</p>
      <h1
        style={{ fontFamily: "Outfit, sans-serif" }}
        className="text-3xl font-light tracking-tight mb-1"
      >
        Error log
      </h1>
      <p className="text-sm text-[#2C332A]/70 mb-6">
        {entries.length} recent {entries.length === 1 ? "issue" : "issues"} captured on this device.
        Tap <b>Copy</b> when emailing a bug report.
      </p>

      <button
        type="button"
        onClick={refresh}
        data-testid="debug-refresh-btn"
        className="text-xs text-[#5F7161] underline underline-offset-2 mb-4"
      >
        Refresh
      </button>

      {entries.length === 0 ? (
        <div
          data-testid="debug-empty"
          className="rounded-2xl border border-[#E6E2D8] bg-white p-8 text-center"
        >
          <p className="text-sm text-[#2C332A]/70">No errors logged — looking healthy.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e, i) => (
            <div
              key={`${e.ts}-${i}`}
              data-testid={`debug-entry-${i}`}
              className="rounded-xl border border-[#E6E2D8] bg-white p-4"
            >
              <div className="flex items-center gap-2 text-xs text-[#2C332A]/60 mb-1.5">
                <AlertCircle size={12} strokeWidth={2} className="text-[#A0454E]" />
                <span>{e.ts}</span>
                <span>·</span>
                <span className="font-mono">{e.source}</span>
                {e.url && <><span>·</span><span>{e.url}</span></>}
              </div>
              <pre className="text-xs whitespace-pre-wrap break-words text-[#2C332A]/85 font-mono leading-snug max-h-40 overflow-y-auto">
                {e.message}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
